import { useEffect, useMemo, useState } from 'react'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { Manifest } from '../api/authoring'
import { creationFields, inlineReady, inlineSchema } from '../model/create'
import { fieldsSchema, typeProse } from '../model/manifest'
import { SchemaForm } from '../editors/SchemaForm'

/**
 * The button that defines a block in place, and the form in front of it
 * where the type needs one.
 *
 * **An inline placement is a first revision**, so a type whose document
 * cannot be empty is filled in before it is placed rather than after.
 * Before this, every picker sent `fields: {}` and the author of a link
 * met "link has no label" from the surface — a true sentence about a
 * question nobody had been asked. It is the same call the pattern setup
 * makes about its references: collect what the first revision cannot be
 * without, so what lands is a usable block rather than an empty one to
 * diagnose.
 *
 * **Most types need no form and this is a bare button**, which is why
 * the ordinary flow is unchanged: a Container, a Grid, a Menu and a
 * photo gallery are all placed on the press and configured where they
 * land.
 *
 * Nothing here names a type. Which types have a form is the manifest's
 * answer, and a tenant's own record with a required field gets one the
 * day it is declared.
 */
export function InlineSetup({
  manifest,
  typeName,
  label,
  busy,
  onPlace,
}: {
  manifest: Manifest | undefined
  typeName: string
  /** The button's words, which differ between the two pickers. */
  label: string
  busy: boolean
  /** The fields the placement carries, empty where the type needs none. */
  onPlace: (fields: Record<string, unknown>) => void
}) {
  const schema = useMemo(() => inlineSchema(manifest, typeName), [manifest, typeName])
  const root = useMemo(() => fieldsSchema(manifest, typeName), [manifest, typeName])
  const [fields, setFields] = useState<Record<string, unknown>>({})

  // Cleared on a type change for the reason the create dialog clears
  // its own: the properties offered are the new type's, and carrying a
  // value across would send one type another's value under a name they
  // happen to share.
  useEffect(() => setFields({}), [typeName])

  const ready = inlineReady(schema, fields)

  if (!schema || !root) {
    return (
      <Stack direction="row">
        <Button variant="contained" disabled={busy || !typeName} onClick={() => onPlace({})}>
          {label}
        </Button>
      </Stack>
    )
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" fontWeight={600}>
            Set up this {typeProse(manifest, typeName).title.toLowerCase()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fill in the fields marked with an asterisk to add this {typeProse(manifest, typeName).title.toLowerCase()}.
            The rest can be filled in later.
          </Typography>
        </Stack>

        <SchemaForm schema={schema} root={root} value={fields} onChange={setFields} disabled={busy} />

        <Stack direction="row">
          <Button
            variant="contained"
            disabled={busy || !typeName || !ready}
            // Sent through the same filter a create uses, so an untouched
            // box is absent from the placement rather than stated as an
            // empty value the author never wrote.
            onClick={() => onPlace(creationFields(fields))}
          >
            {label}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
